/**
 * Every read and write the screens make, as TanStack Query hooks. Keys are hierarchical
 * (["contact", id], ["contacts", filters]) so a write invalidates exactly what it changed and
 * nothing the person is not looking at.
 */
import type {
  Activity,
  AgentOverall,
  AuditEntry,
  BsiDelivery,
  CalendarEvent,
  CallDetail,
  CommissionFactor,
  CommissionImportResult,
  CommissionRule,
  Cockpit,
  Contact,
  ContactRecord,
  ContactSummary,
  FloorView,
  HomeDashboard,
  IntegrationClient,
  Lead,
  LeadSummary,
  LiveCall,
  NamedItem,
  Page,
  PipelineBoard,
  Picklists,
  Policy,
  PolicySummary,
  RecalculationResult,
  RecordScope,
  ReportDefinition,
  ReportResult,
  SalesGoal,
  SearchResults,
  SettingItem,
  StageChange,
  Task,
  TeamDashboard,
  User,
  VendorPolicy,
} from "@tlcrm/shared";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

const MINUTE = 60_000;

/* ---- Reference data ------------------------------------------------------------------------ */

export function usePicklists() {
  return useQuery({
    queryKey: ["picklists"],
    queryFn: ({ signal }) => api<Picklists>("/picklists", { signal }),
    staleTime: 30 * MINUTE,
  });
}

export function useUsers() {
  return useQuery({
    queryKey: ["users"],
    queryFn: ({ signal }) => api<{ items: User[] }>("/users", { signal }).then((r) => r.items),
    staleTime: 30 * MINUTE,
  });
}

export function useSearch(q: string) {
  return useQuery({
    queryKey: ["search", q],
    queryFn: ({ signal }) => api<SearchResults>("/search", { query: { q }, signal }),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/* ---- Contacts ------------------------------------------------------------------------------ */

export function useContacts(filters: { q?: string; stage?: string; owner?: string }) {
  return useInfiniteQuery({
    queryKey: ["contacts", filters],
    queryFn: ({ pageParam, signal }) =>
      api<Page<ContactSummary>>("/contacts", {
        query: { ...filters, cursor: pageParam, limit: 50 },
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

export function useContact(id: string) {
  return useQuery({
    queryKey: ["contact", id],
    queryFn: ({ signal }) => api<ContactRecord>(`/contacts/${id}`, { signal }),
  });
}

export function useTimeline(kind: "contacts" | "leads" | "policies", id: string) {
  return useInfiniteQuery({
    queryKey: ["timeline", kind, id],
    queryFn: ({ pageParam, signal }) =>
      api<Page<Activity>>(`/${kind}/${id}/timeline`, {
        query: { cursor: pageParam, limit: 30 },
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function useAudit(entity: string, id: string, enabled: boolean) {
  return useQuery({
    queryKey: ["audit", entity, id],
    queryFn: ({ signal }) =>
      api<{ items: AuditEntry[] }>("/audit", { query: { entity, id }, signal }).then(
        (r) => r.items,
      ),
    enabled,
  });
}

/** Invalidate everything a change to one contact can show up in. */
function useInvalidateContact() {
  const qc = useQueryClient();
  return (id: string) =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["contact", id] }),
      qc.invalidateQueries({ queryKey: ["contacts"] }),
      qc.invalidateQueries({ queryKey: ["timeline", "contacts", id] }),
      qc.invalidateQueries({ queryKey: ["audit", "contact", id] }),
      qc.invalidateQueries({ queryKey: ["search"] }),
    ]);
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Contact>("/contacts", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contacts"] }),
  });
}

export function useUpdateContact(id: string) {
  const invalidate = useInvalidateContact();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Contact>(`/contacts/${id}`, { method: "PATCH", body }),
    onSettled: () => invalidate(id),
  });
}

export function useAddPhone(contactId: string) {
  const invalidate = useInvalidateContact();
  return useMutation({
    mutationFn: (body: { e164: string; kind: string; isPrimary?: boolean }) =>
      api<Contact>(`/contacts/${contactId}/phones`, { method: "POST", body }),
    onSuccess: () => invalidate(contactId),
  });
}

export function useUpdatePhone(contactId: string) {
  const invalidate = useInvalidateContact();
  return useMutation({
    mutationFn: ({
      phoneId,
      ...body
    }: {
      phoneId: string;
      isPrimary?: true;
      wrongNumber?: boolean;
      kind?: string;
    }) => api<Contact>(`/contacts/${contactId}/phones/${phoneId}`, { method: "PATCH", body }),
    onSuccess: () => invalidate(contactId),
  });
}

export function useAddNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { body: string; contactId?: string; leadId?: string; policyId?: string }) =>
      api<Activity>("/notes", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["timeline"] }),
  });
}

/* ---- Leads --------------------------------------------------------------------------------- */

export function useLeads(filters: {
  status: string;
  owner?: string;
  source?: string;
  callType?: string;
  today?: string;
}) {
  return useInfiniteQuery({
    queryKey: ["leads", filters],
    queryFn: ({ pageParam, signal }) =>
      api<Page<LeadSummary>>("/leads", {
        query: { ...filters, cursor: pageParam, limit: 50 },
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    refetchInterval: MINUTE,
  });
}

export function useLead(id: string) {
  return useQuery({
    queryKey: ["lead", id],
    queryFn: ({ signal }) => api<Lead>(`/leads/${id}`, { signal }),
  });
}

export function useUpdateLead(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Lead>(`/leads/${id}`, { method: "PATCH", body }),
    onSettled: (lead) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["lead", id] }),
        qc.invalidateQueries({ queryKey: ["leads"] }),
        qc.invalidateQueries({ queryKey: ["dashboard", "pipeline"] }),
        lead && qc.invalidateQueries({ queryKey: ["contact", lead.contactId] }),
      ]),
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api<Lead>("/leads", { method: "POST", body }),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["leads"] }),
        qc.invalidateQueries({ queryKey: ["contacts"] }),
      ]),
  });
}

/* ---- Policies ------------------------------------------------------------------------------ */

export interface PolicyFilters {
  view?: string;
  scope?: RecordScope;
  q?: string;
}

export function usePolicies(filters: PolicyFilters) {
  return useInfiniteQuery({
    queryKey: ["policies", filters],
    queryFn: ({ pageParam, signal }) =>
      api<Page<PolicySummary>>("/policies", {
        query: { ...filters, cursor: pageParam, limit: 100 },
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

export function usePolicy(id: string) {
  return useQuery({
    queryKey: ["policy", id],
    queryFn: ({ signal }) => api<Policy>(`/policies/${id}`, { signal }),
  });
}

/** A saved policy touches lists, dashboards, its customer and its own history. */
function useInvalidatePolicy() {
  const qc = useQueryClient();
  return (policy?: Policy) =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["policies"] }),
      qc.invalidateQueries({ queryKey: ["dashboard"] }),
      qc.invalidateQueries({ queryKey: ["report"] }),
      policy && qc.invalidateQueries({ queryKey: ["policy", policy.id] }),
      policy && qc.invalidateQueries({ queryKey: ["audit", "policy", policy.id] }),
      policy && qc.invalidateQueries({ queryKey: ["contact", policy.customerId] }),
      qc.invalidateQueries({ queryKey: ["tasks"] }),
    ]);
}

export function useCreatePolicy() {
  const invalidate = useInvalidatePolicy();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Policy>("/policies", { method: "POST", body }),
    onSuccess: (policy) => invalidate(policy),
  });
}

export function useUpdatePolicy(id: string) {
  const invalidate = useInvalidatePolicy();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Policy>(`/policies/${id}`, { method: "PATCH", body }),
    onSuccess: (policy) => qc.setQueryData(["policy", id], policy),
    onSettled: (policy) => invalidate(policy),
  });
}

export function useStageHistory(id: string) {
  return useQuery({
    queryKey: ["policy", id, "stages"],
    queryFn: ({ signal }) =>
      api<{ items: StageChange[] }>(`/policies/${id}/stage-history`, { signal }).then(
        (r) => r.items,
      ),
  });
}

export function useRecalculatePolicy(id: string) {
  const invalidate = useInvalidatePolicy();
  return useMutation({
    mutationFn: () => api<Policy>(`/policies/${id}/recalculate`, { method: "POST" }),
    onSuccess: (policy) => invalidate(policy),
  });
}

export function useAddParty(policyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Policy>(`/policies/${policyId}/parties`, { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["policy", policyId] }),
  });
}

export function useVendorPolicies() {
  return useQuery({
    queryKey: ["vendor-policies"],
    queryFn: ({ signal }) =>
      api<{ items: VendorPolicy[] }>("/vendor/policies", { signal }).then((r) => r.items),
  });
}

/* ---- Lead wrap-up and conversion ----------------------------------------------------------- */

function useInvalidateLead() {
  const qc = useQueryClient();
  return (id: string, contactId?: string) =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["lead", id] }),
      qc.invalidateQueries({ queryKey: ["leads"] }),
      qc.invalidateQueries({ queryKey: ["audit", "lead", id] }),
      qc.invalidateQueries({ queryKey: ["dashboard", "pipeline"] }),
      contactId && qc.invalidateQueries({ queryKey: ["contact", contactId] }),
    ]);
}

export function useLeadOutcome(id: string) {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Lead>(`/leads/${id}/outcome`, { method: "POST", body }),
    onSuccess: (lead) => invalidate(id, lead.contactId),
  });
}

export function useConvertLead(id: string) {
  const invalidateLead = useInvalidateLead();
  const invalidatePolicy = useInvalidatePolicy();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Policy>(`/leads/${id}/convert`, { method: "POST", body }),
    onSuccess: (policy) =>
      Promise.all([invalidateLead(id, policy.customerId), invalidatePolicy(policy)]),
  });
}

export function useBulkLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ changed: number }>("/leads/bulk", { method: "POST", body }),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["leads"] }),
        qc.invalidateQueries({ queryKey: ["dashboard", "cockpit"] }),
        qc.invalidateQueries({ queryKey: ["dashboard", "pipeline"] }),
      ]),
  });
}

/* ---- Dashboards and reports ---------------------------------------------------------------- */

const LIVE = { staleTime: 60_000, refetchInterval: 5 * MINUTE, placeholderData: keepPreviousData };

export function useHomeDashboard() {
  return useQuery({
    queryKey: ["dashboard", "home"],
    queryFn: ({ signal }) => api<HomeDashboard>("/dashboards/home", { signal }),
    ...LIVE,
  });
}

/** The agent's cockpit: what to do next and how the week is going. A minute fresh at most. */
export function useCockpit() {
  return useQuery({
    queryKey: ["dashboard", "cockpit"],
    queryFn: ({ signal }) => api<Cockpit>("/cockpit", { signal }),
    staleTime: 30_000,
    refetchInterval: MINUTE,
    placeholderData: keepPreviousData,
  });
}

/** The pipeline board, for me or a manager's team or branch; half a minute fresh. */
export function usePipeline(scope: RecordScope) {
  return useQuery({
    queryKey: ["dashboard", "pipeline", scope],
    queryFn: ({ signal }) => api<PipelineBoard>("/pipeline", { query: { scope }, signal }),
    staleTime: 15_000,
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
}

/** A manager's floor: what needs them now. */
export function useFloor(enabled: boolean) {
  return useQuery({
    queryKey: ["dashboard", "floor"],
    queryFn: ({ signal }) => api<FloorView>("/cockpit/floor", { signal }),
    enabled,
    staleTime: 30_000,
    refetchInterval: MINUTE,
  });
}

/** The call the agent is on, from the desk's events; polled, since a call starts on its own. */
export function useLiveCall() {
  return useQuery({
    queryKey: ["calls", "live"],
    queryFn: ({ signal }) =>
      api<{ call: LiveCall | null }>("/calls/live", { signal }).then((r) => r.call),
    refetchInterval: 5_000,
    staleTime: 0,
  });
}

export function useAgentDashboard(agentId: string | null) {
  return useQuery({
    queryKey: ["dashboard", "agent", agentId],
    queryFn: ({ signal }) => api<AgentOverall>(`/dashboards/agents/${agentId}`, { signal }),
    enabled: Boolean(agentId),
    ...LIVE,
  });
}

export function useTeamDashboard(teamId: string | null) {
  return useQuery({
    queryKey: ["dashboard", "team", teamId],
    queryFn: ({ signal }) => api<TeamDashboard>("/dashboards/team", { query: { teamId }, signal }),
    ...LIVE,
  });
}

export function useReports() {
  return useQuery({
    queryKey: ["reports"],
    queryFn: ({ signal }) =>
      api<{ items: ReportDefinition[] }>("/reports", { signal }).then((r) => r.items),
    staleTime: 30 * MINUTE,
  });
}

export function useReport(key: string | null, scope?: RecordScope) {
  return useQuery({
    queryKey: ["report", key, scope],
    queryFn: ({ signal }) => api<ReportResult>(`/reports/${key}`, { query: { scope }, signal }),
    enabled: Boolean(key),
    ...LIVE,
  });
}

/* ---- Calendar ------------------------------------------------------------------------------ */

export function useEvents(range: { from: string; to: string; assignee?: string }) {
  return useQuery<CalendarEvent[]>({
    queryKey: ["events", range],
    queryFn: ({ signal }) =>
      api<{ items: CalendarEvent[] }>("/events", { query: range, signal }).then((r) => r.items),
    placeholderData: keepPreviousData,
  });
}

export function useSaveEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id
        ? api<CalendarEvent>(`/events/${id}`, { method: "PATCH", body })
        : api<CalendarEvent>("/events", { method: "POST", body }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/events/${id}`, { method: "DELETE" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}

/* ---- Commission ---------------------------------------------------------------------------- */

export function useCommissionRules() {
  return useQuery({
    queryKey: ["commission", "rules"],
    queryFn: ({ signal }) =>
      api<{ items: CommissionRule[] }>("/commission/rules", { signal }).then((r) => r.items),
  });
}

export function useCommissionFactors() {
  return useQuery({
    queryKey: ["commission", "factors"],
    queryFn: ({ signal }) =>
      api<{ items: CommissionFactor[] }>("/commission/factors", { signal }).then((r) => r.items),
  });
}

/** Any rate change recalculates the book, so everything that shows a commission refetches. */
function useInvalidateCommission() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["commission"] }),
      qc.invalidateQueries({ queryKey: ["picklists"] }),
      qc.invalidateQueries({ queryKey: ["policies"] }),
      qc.invalidateQueries({ queryKey: ["policy"] }),
      qc.invalidateQueries({ queryKey: ["dashboard"] }),
      qc.invalidateQueries({ queryKey: ["report"] }),
    ]);
}

export function useImportRules() {
  const invalidate = useInvalidateCommission();
  return useMutation({
    mutationFn: (csv: string) =>
      api<CommissionImportResult & { recalculation: RecalculationResult }>(
        "/commission/rules/import",
        { method: "POST", body: { csv } },
      ),
    onSuccess: () => invalidate(),
  });
}

export function useSaveRule() {
  const invalidate = useInvalidateCommission();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id
        ? api<{ rule: CommissionRule; recalculation: RecalculationResult }>(
            `/commission/rules/${id}`,
            { method: "PATCH", body },
          )
        : api<{ rule: CommissionRule; recalculation: RecalculationResult }>("/commission/rules", {
            method: "POST",
            body,
          }),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteRule() {
  const invalidate = useInvalidateCommission();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ recalculation: RecalculationResult }>(`/commission/rules/${id}`, { method: "DELETE" }),
    onSuccess: () => invalidate(),
  });
}

export function useSaveFactor() {
  const invalidate = useInvalidateCommission();
  return useMutation({
    mutationFn: (body: { carrierId: string | null; productId: string | null; factor: number }) =>
      api<{ factor: CommissionFactor; recalculation: RecalculationResult }>("/commission/factors", {
        method: "PUT",
        body,
      }),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteFactor() {
  const invalidate = useInvalidateCommission();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ recalculation: RecalculationResult }>(`/commission/factors/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => invalidate(),
  });
}

export function useRecalculateAll() {
  const invalidate = useInvalidateCommission();
  return useMutation({
    mutationFn: () => api<RecalculationResult>("/commission/recalculate", { method: "POST" }),
    onSuccess: () => invalidate(),
  });
}

/* ---- Admin --------------------------------------------------------------------------------- */

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: ({ signal }) =>
      api<{ items: User[] }>("/admin/users", { signal }).then((r) => r.items),
  });
}

export function useSaveUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id
        ? api<User>(`/admin/users/${id}`, { method: "PATCH", body })
        : api<User>("/admin/users", { method: "POST", body }),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["admin", "users"] }),
        qc.invalidateQueries({ queryKey: ["users"] }),
      ]),
  });
}

export type AdminListKind = "carriers" | "products" | "lead-vendors" | "teams";

export function useAdminList(kind: AdminListKind) {
  return useQuery({
    queryKey: ["admin", "list", kind],
    queryFn: ({ signal }) =>
      api<{ items: (NamedItem & { members?: number })[] }>(`/admin/lists/${kind}`, {
        signal,
      }).then((r) => r.items),
  });
}

export function useSaveListItem(kind: AdminListKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id
        ? api<NamedItem>(`/admin/lists/${kind}/${id}`, { method: "PATCH", body })
        : api<NamedItem>(`/admin/lists/${kind}`, { method: "POST", body }),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["admin", "list", kind] }),
        qc.invalidateQueries({ queryKey: ["picklists"] }),
      ]),
  });
}

export function useSettings() {
  return useQuery({
    queryKey: ["admin", "settings"],
    queryFn: ({ signal }) =>
      api<{ items: SettingItem[] }>("/admin/settings", { signal }).then((r) => r.items),
  });
}

export function useSaveSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      api<{ items: SettingItem[] }>(`/admin/settings/${key}`, { method: "PUT", body: { value } }),
    onSuccess: (r) => qc.setQueryData(["admin", "settings"], r.items),
    onSettled: () => qc.invalidateQueries({ queryKey: ["dashboard"] }),
  });
}

export function useGoals() {
  return useQuery({
    queryKey: ["admin", "goals"],
    queryFn: ({ signal }) =>
      api<{ items: SalesGoal[] }>("/admin/goals", { signal }).then((r) => r.items),
  });
}

export function useSaveGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { teamId: string | null; weekStart: string | null; amount: number }) =>
      api<SalesGoal>("/admin/goals", { method: "PUT", body }),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["admin", "goals"] }),
        qc.invalidateQueries({ queryKey: ["dashboard"] }),
      ]),
  });
}

export function useDeleteGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/admin/goals/${id}`, { method: "DELETE" }),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["admin", "goals"] }),
        qc.invalidateQueries({ queryKey: ["dashboard"] }),
      ]),
  });
}

export function useClients() {
  return useQuery({
    queryKey: ["admin", "clients"],
    queryFn: ({ signal }) =>
      api<{ items: IntegrationClient[] }>("/admin/clients", { signal }).then((r) => r.items),
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; scopes: string[]; leadVendorId: string | null }) =>
      api<{ id: string; token: string }>("/admin/clients", { method: "POST", body }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "clients"] }),
  });
}

export function useRevokeClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/admin/clients/${id}`, { method: "DELETE" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "clients"] }),
  });
}

export function useBsiDeliveries(status?: string) {
  return useQuery({
    queryKey: ["admin", "bsi", status],
    queryFn: ({ signal }) =>
      api<{ items: BsiDelivery[] }>("/admin/bsi", { query: { status }, signal }).then(
        (r) => r.items,
      ),
    refetchInterval: MINUTE,
  });
}

export function useRequeueDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/admin/bsi/${id}/requeue`, { method: "POST" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "bsi"] }),
  });
}

/* ---- Tasks --------------------------------------------------------------------------------- */

export function useTasks(filters: {
  due?: string;
  status?: string;
  assignee?: string;
  contactId?: string;
}) {
  return useQuery({
    queryKey: ["tasks", filters],
    queryFn: ({ signal }) =>
      api<Page<Task>>("/tasks", { query: { ...filters, limit: 100 }, signal }).then((p) => p.items),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api<Task>(`/tasks/${id}`, { method: "PATCH", body }),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["tasks"] }),
        qc.invalidateQueries({ queryKey: ["contact"] }),
        qc.invalidateQueries({ queryKey: ["timeline"] }),
        qc.invalidateQueries({ queryKey: ["dashboard", "cockpit"] }),
        qc.invalidateQueries({ queryKey: ["dashboard", "pipeline"] }),
      ]),
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api<Task>("/tasks", { method: "POST", body }),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["tasks"] }),
        qc.invalidateQueries({ queryKey: ["contact"] }),
      ]),
  });
}

/* ---- Calls --------------------------------------------------------------------------------- */

export type UnmatchedCall = Pick<
  CallDetail,
  | "id"
  | "direction"
  | "phone"
  | "queue"
  | "line"
  | "startedAt"
  | "endedAt"
  | "durationSeconds"
  | "disposition"
  | "reason"
  | "summary"
  | "agent"
>;

export function useUnmatchedCalls() {
  return useQuery({
    queryKey: ["calls", "unmatched"],
    queryFn: ({ signal }) =>
      api<{ items: UnmatchedCall[] }>("/calls/unmatched", { signal }).then((r) => r.items),
    refetchInterval: MINUTE,
  });
}

export function useLinkCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, contactId }: { callId: string; contactId: string }) =>
      api<{ linked: number }>(`/calls/${callId}/link`, { method: "POST", body: { contactId } }),
    onSuccess: () => qc.invalidateQueries(),
  });
}
